// Portfolio cripto — holdings + precios live de CoinGecko + total + allocation.
// Add modal con autocomplete por nombre/símbolo. Refresh automático cada 60s.

import { useEffect, useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { AppLayout } from '../components/AppLayout';
import { Button } from '../components/ui/Button';
import { Card, Stat, Section } from '../components/ui/Card';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { cn } from '../lib/cn';

interface Holding {
  id: string;
  coingeckoId: string;
  symbol: string;
  name: string;
  amount: number;
  priceUsd: number | null;
  valueUsd: number | null;
  change24h: number | null;
  marketCap: number | null;
  allocation: number | null;
  priceUpdatedAt: string | null;
  acquiredAt: string | null;
  notes: string | null;
  createdAt: string;
}

interface PortfolioResponse {
  holdings: Holding[];
  totalUsd: number;
  count: number;
  topGainer: Holding | null;
  topLoser: Holding | null;
  pricesUpdatedAt: string;
}

interface CoinResult {
  id: string;
  symbol: string;
  name: string;
  thumb?: string;
  large?: string;
  market_cap_rank: number | null;
}

const fmtUsd = (n: number | null, opts: Intl.NumberFormatOptions = {}): string => {
  if (n == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
    ...opts,
  }).format(n);
};

const fmtAmount = (n: number): string =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 8 }).format(n);

const fmtPct = (n: number | null): string => {
  if (n == null) return '—';
  const sign = n >= 0 ? '+' : '';
  return `${sign}${n.toFixed(2)}%`;
};

export default function Portfolio(): ReactElement {
  const [creating, setCreating] = useState(false);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['portfolio'],
    queryFn: () => api<PortfolioResponse>('/portfolio'),
    refetchInterval: 60 * 1000,
    staleTime: 30 * 1000,
  });

  const deleteHolding = useMutation({
    mutationFn: (id: string) => api<void>(`/portfolio/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portfolio'] }),
  });

  const empty = !isLoading && data && data.holdings.length === 0;

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8 py-8 sm:py-10">
        <header className="mb-8 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-[11px] text-ink-subtle tracking-[0.22em] uppercase mb-2">
              Portafolio · cripto
            </div>
            <h1 className="font-display text-4xl tracking-tight text-ink leading-tight">
              Lo que tenés<span className="text-accent-500">,</span> ahora.
            </h1>
            <p className="text-ink-muted mt-2 text-sm">
              Holdings cripto con precios en vivo desde CoinGecko. Sumá assets manualmente,
              actualizá amounts. La data del journal de trades vive en su propia sección.
            </p>
          </div>
          <Button variant="primary" onClick={() => setCreating(true)}>
            + Sumar asset
          </Button>
        </header>

        {empty ? (
          <Card padded={false} className="p-12 text-center border-dashed">
            <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gold-sheen flex items-center justify-center text-stone-950 text-2xl font-bold">
              ◇
            </div>
            <h3 className="font-display text-2xl text-ink mb-2">Empezá tu portafolio</h3>
            <p className="text-sm text-ink-muted mb-6 max-w-md mx-auto">
              Agregá los assets que tenés (BTC, ETH, lo que sea) con su cantidad. La app
              fetchea precio y % 24h en vivo desde CoinGecko.
            </p>
            <Button variant="primary" onClick={() => setCreating(true)}>
              + Agregar primer asset
            </Button>
          </Card>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
              <Stat
                label="Valor total"
                value={data ? fmtUsd(data.totalUsd) : '—'}
                hint={data ? `${data.count} asset${data.count === 1 ? '' : 's'}` : ' '}
                tone="accent"
              />
              <Stat
                label="Top gainer 24h"
                value={data?.topGainer ? data.topGainer.symbol : '—'}
                hint={data?.topGainer ? fmtPct(data.topGainer.change24h) : ' '}
                tone="win"
                trend={data?.topGainer ? 'up' : null}
              />
              <Stat
                label="Top loser 24h"
                value={data?.topLoser ? data.topLoser.symbol : '—'}
                hint={data?.topLoser ? fmtPct(data.topLoser.change24h) : ' '}
                tone="loss"
                trend={data?.topLoser ? 'down' : null}
              />
              <Stat
                label="Actualizado"
                value={data?.pricesUpdatedAt ? new Date(data.pricesUpdatedAt).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' }) : '—'}
                hint="precios CoinGecko · refresh 60s"
                mono={false}
              />
            </div>

            <Section title="Holdings">
              <Card padded={false}>
                <div className="hidden md:grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr_auto] gap-3 px-5 py-3 border-b border-surface-border text-[10px] uppercase tracking-[0.18em] text-ink-subtle font-semibold">
                  <div>Asset</div>
                  <div className="text-right">Cantidad</div>
                  <div className="text-right">Precio</div>
                  <div className="text-right">24h</div>
                  <div className="text-right">Valor · Allocation</div>
                  <div></div>
                </div>
                <div className="divide-y divide-surface-border">
                  {(data?.holdings ?? []).map((h) => (
                    <HoldingRow
                      key={h.id}
                      h={h}
                      onDelete={() => {
                        if (confirm(`¿Eliminar ${h.symbol} del portafolio?`)) {
                          deleteHolding.mutate(h.id);
                        }
                      }}
                    />
                  ))}
                </div>
              </Card>
            </Section>
          </>
        )}
      </div>

      {creating && (
        <AddHoldingModal
          onClose={() => setCreating(false)}
          onCreated={() => {
            qc.invalidateQueries({ queryKey: ['portfolio'] });
            setCreating(false);
          }}
        />
      )}
    </AppLayout>
  );
}

function HoldingRow({ h, onDelete }: { h: Holding; onDelete: () => void }): ReactElement {
  const changeColor =
    h.change24h == null
      ? 'text-ink-subtle'
      : h.change24h > 0
      ? 'text-win-DEFAULT'
      : h.change24h < 0
      ? 'text-loss-DEFAULT'
      : 'text-ink-muted';

  return (
    <div className="grid grid-cols-[1fr_auto] md:grid-cols-[1.5fr_1fr_1fr_1fr_1fr_auto] gap-3 px-5 py-3.5 items-center hover:bg-surface-panel/40 transition-colors">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-9 h-9 rounded-lg bg-surface-panel border border-surface-border flex items-center justify-center font-mono font-bold text-xs text-accent-500 shrink-0">
          {h.symbol.slice(0, 3)}
        </div>
        <div className="min-w-0">
          <div className="font-semibold text-sm text-ink truncate">{h.name}</div>
          <div className="text-[10px] uppercase tracking-wider text-ink-subtle font-mono">
            {h.symbol}
          </div>
        </div>
      </div>
      <div className="hidden md:block text-right font-mono text-sm tabular-nums text-ink">
        {fmtAmount(h.amount)}
      </div>
      <div className="hidden md:block text-right font-mono text-sm tabular-nums text-ink-muted">
        {fmtUsd(h.priceUsd)}
      </div>
      <div className={cn('hidden md:block text-right font-mono text-sm tabular-nums font-semibold', changeColor)}>
        {fmtPct(h.change24h)}
      </div>
      <div className="hidden md:block text-right">
        <div className="font-mono text-sm font-bold text-ink tabular-nums">
          {fmtUsd(h.valueUsd)}
        </div>
        {h.allocation != null && (
          <div className="text-[10px] text-ink-faint mt-0.5 font-mono">
            {(h.allocation * 100).toFixed(1)}%
          </div>
        )}
      </div>
      <div className="md:hidden text-right min-w-0">
        <div className="font-mono text-sm font-bold text-ink tabular-nums">{fmtUsd(h.valueUsd)}</div>
        <div className={cn('font-mono text-xs tabular-nums', changeColor)}>
          {fmtPct(h.change24h)} · {fmtAmount(h.amount)} {h.symbol}
        </div>
      </div>
      <button
        onClick={onDelete}
        className="text-ink-faint hover:text-loss-DEFAULT transition-colors w-8 h-8 flex items-center justify-center rounded-md hover:bg-loss-DEFAULT/10"
        aria-label="Eliminar"
        title="Eliminar"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
        </svg>
      </button>
    </div>
  );
}

function AddHoldingModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}): ReactElement {
  const [query, setQuery] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [selected, setSelected] = useState<CoinResult | null>(null);
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(query.trim()), 350);
    return () => clearTimeout(t);
  }, [query]);

  const { data: searchData } = useQuery({
    queryKey: ['portfolio-coin-search', debouncedQ],
    queryFn: () =>
      api<{ coins: CoinResult[] }>(
        `/portfolio/coins/search?q=${encodeURIComponent(debouncedQ)}`,
      ),
    enabled: debouncedQ.length >= 2 && !selected,
    staleTime: 60 * 1000,
  });

  const submit = async (): Promise<void> => {
    if (!selected || !amount) return;
    const n = Number(amount);
    if (!Number.isFinite(n) || n <= 0) {
      setError('La cantidad debe ser un número positivo');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api('/portfolio', {
        method: 'POST',
        body: {
          coingeckoId: selected.id,
          symbol: selected.symbol,
          name: selected.name,
          amount: n,
          notes: notes.trim() || undefined,
        },
      });
      onCreated();
    } catch (e) {
      setError((e as Error).message ?? 'Error al guardar');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Sumar asset al portafolio"
      description="Buscá el coin por nombre o símbolo. Precios vienen en vivo de CoinGecko."
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={!selected || !amount || submitting}
            loading={submitting}
          >
            Guardar
          </Button>
        </div>
      }
    >
      <div className="space-y-4 min-w-[300px] sm:min-w-[420px]">
        {!selected ? (
          <>
            <Input
              label="Buscar coin"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="bitcoin, eth, sol..."
              autoFocus
            />
            {debouncedQ.length >= 2 && (
              <div className="max-h-72 overflow-y-auto rounded-lg border border-surface-border bg-surface-panel/30 divide-y divide-surface-border">
                {(searchData?.coins ?? []).length === 0 ? (
                  <div className="text-xs text-ink-faint italic p-3">
                    Sin resultados.
                  </div>
                ) : (
                  (searchData?.coins ?? []).map((c) => (
                    <button
                      key={c.id}
                      onClick={() => setSelected(c)}
                      className="w-full text-left p-2.5 flex items-center gap-3 hover:bg-surface-panel/70 transition-colors"
                    >
                      {c.thumb ? (
                        <img
                          src={c.thumb}
                          alt=""
                          className="w-7 h-7 rounded-full bg-surface-base"
                          loading="lazy"
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-surface-panel border border-surface-border" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-ink truncate">{c.name}</div>
                        <div className="text-[10px] uppercase font-mono tracking-wider text-ink-subtle">
                          {c.symbol}
                        </div>
                      </div>
                      {c.market_cap_rank && (
                        <div className="text-[10px] font-mono text-ink-faint">
                          #{c.market_cap_rank}
                        </div>
                      )}
                    </button>
                  ))
                )}
              </div>
            )}
          </>
        ) : (
          <div className="space-y-4">
            <Card tone="accent" padded={false} className="p-3 flex items-center gap-3">
              {selected.thumb && (
                <img
                  src={selected.thumb}
                  alt=""
                  className="w-9 h-9 rounded-full"
                  referrerPolicy="no-referrer"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-ink truncate">{selected.name}</div>
                <div className="text-[10px] uppercase font-mono tracking-wider text-ink-subtle">
                  {selected.symbol} · {selected.id}
                </div>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="text-xs text-ink-muted hover:text-ink underline-offset-2 hover:underline"
              >
                cambiar
              </button>
            </Card>
            <Input
              label="Cantidad"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={`p.ej. 0.5 ${selected.symbol.toUpperCase()}`}
              type="number"
              step="any"
              min="0"
              autoFocus
            />
            <div>
              <label className="block text-xs font-semibold text-ink-muted uppercase tracking-wider mb-1.5">
                Notas <span className="text-ink-faint">(opcional)</span>
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 rounded-lg bg-surface-elevated border border-surface-border text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-accent-500/60"
                placeholder="cold wallet, exchange X, etc."
              />
            </div>
            {error && <div className="text-xs text-loss-DEFAULT">{error}</div>}
          </div>
        )}
      </div>
    </Modal>
  );
}
